// src/components/RevueCvAo.jsx — LA REVUE DES CV D'UN DOSSIER AO.
//
// Demande 30.10 : « qu'il me sorte le CV, je le mets dans Claude, je le
// challenge, et soit je valide, soit je l'écrase » — « un truc comme le
// tutoriel ». Même grammaire que la bulle du tutoriel (src/tutoriel) : un
// calque qui ne bloque rien, la découpe sur la ligne RÉELLE du profil dans
// le dossier, une bulle qui dit quoi faire, un sommaire « où j'en suis ».
//
// Tout ce qui compte est SERVI (GET /ao/:id/revue-cv) : le CV retenu (celui
// qui partira), son état, le texte à copier. Valider est la relecture du
// dossier (« relu » dans le Market Ready aussi) ; remplacer écrase le CV du
// besoin partout. L'écran ne recalcule rien.
//
// CHACUN SES LOTS (30.10 : « choisir quel lot je travaille, pour travailler
// en équipe »). On prend ses lots (un relecteur par lot, nommé pour tous) ;
// la revue ne promène que dans les siens. Le lot d'un collègue se reprend en
// le disant (409 `lot_pris`, confirmé, puis `forcer`). Un serveur qui ne
// connaît pas encore les lots (pas de `mes_lots`) : tout le dossier, comme avant.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import APIService from '../services/api';
import { avecCle } from '../services/cleLecture';
import PointsAConfirmer from './optimiseur/PointsAConfirmer';
import QuestionsCandidat from './optimiseur/QuestionsCandidat';
import AjoutsValides from './optimiseur/AjoutsValides';
import ChargementAtomes from './ChargementAtomes';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} aria-hidden="true" />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const MARGE = 12;
// Les lots que la revue parcourt : les miens quand le serveur connaît les lots.
const lotsParcourus = (j) => (Array.isArray(j?.mes_lots) ? (j.lots || []).filter(l => l.mien) : (j?.lots || []));
const aplatir = (j) => lotsParcourus(j).flatMap(l => l.profils.map(p => ({ ...p, lot: l.titre })));
const relecteurDe = (l) => (l.mien ? 'Vous' : l.relecteur ? (l.relecteur.nom || 'Un collègue') : null);

const TON = { valide: 'var(--erp-success)', change: 'var(--erp-warning)', a_revoir: 'var(--erp-text-secondary)', sans_cv: 'var(--erp-danger)' };
const ICONE = { valide: 'check-circle-fill', change: 'arrow-repeat', a_revoir: 'circle', sans_cv: 'exclamation-circle' };

const cleDe = (p) => (p ? `${p.besoin_id}:${p.candidate_id}` : '');
const ONGLETS = [['analyse', 'Analyse'], ['corrigee', 'Version corrigée'], ['historique', 'Historique']];
const REPONSE = { confirme: 'confirmé par le candidat', infirme: '« Non » — écart à aborder en entretien' };
const borne = (v) => Math.max(0, Math.min(100, Math.round(Number(v) || 0)));
const dateCourte = (d) => { const x = d ? new Date(d) : null; return x && !Number.isNaN(x.getTime()) ? x.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''; };
const dateHeure = (d) => { const x = d ? new Date(d) : null; return x && !Number.isNaN(x.getTime()) ? `${dateCourte(d)} · ${x.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}` : ''; };
const taille = (o) => { const n = Number(o); if (!n) return ''; return n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} Ko` : `${(n / 1024 / 1024).toFixed(1).replace('.', ',')} Mo`; };
const estCv = (d) => (typeof d.est_cv === 'boolean' ? d.est_cv : /cv/i.test(`${d.type_piece || ''} ${d.label || ''} ${d.original_name || ''}`));
// LE BRUIT (31.10 : « masquer le bruit ») : une action à moins d'un point, ou
// un savoir-être laissé « à observer en entretien », se replie sous « Voir les
// détails ». Ce qui débloque le bloc des indispensables passe en premier.
const estSavoirEtre = (r) => /observer en entretien|savoir.?[êe]tre/i.test(String(r?.preuve || ''));
const estBruit = (r) => !r.debloque && r.groupe !== 'indispensables' && ((Number(r.gain) || 0) < 1 || estSavoirEtre(r));
// Ce qu'il faut demander : la question servie, sinon la question d'usage.
const questionDe = (critere) => `Avez-vous pratiqué « ${critere} » ? Dans quelle mission, combien de temps, avec quel résultat ?`;
// La grille du calcul (points sur 100), dite comme la fiche la dit : les
// poids à 0 se taisent ; la source dit si c'est le poste ou la grille par défaut.
const GRILLE = [['primordiales', 'indispensables'], ['importantes', 'importantes'], ['role', 'rôle'], ['optionnelles', 'optionnelles'], ['formation', 'formation']];
const ligneGrille = (g) => {
  if (!g || typeof g !== 'object') return null;
  const parts = GRILLE.filter(([k]) => Number(g[k]) > 0).map(([k, l]) => `${l} ${Math.round(Number(g[k]))}`);
  if (!parts.length) return null;
  return `Grille : ${parts.join(' · ')} — ${g.source === 'fiche' ? 'pondération du poste' : 'grille par défaut'}`;
};
const critereDeSuggestion = (sg) => String(sg.titre).replace(/^Faire apparaître\s*/i, '').replace(/^Rattacher\s*/i, '').replace(/\s*(si l'expérience existe|à une mission — à confirmer avec le candidat)$/i, '').replace(/^«\s*|\s*»$/g, '');

// LA SIGNATURE : la barre de niveau d'une exigence — la part que le CV montre
// (encre), la part que l'action ajouterait (corail pâle, jusqu'à la marche
// visée), les marches 40 / 75 en repères.
function Niveau({ niveau, cible }) {
  const fait = borne(niveau);
  const vise = Math.max(fait, borne(cible ?? niveau));
  return (
    <div className="opt-niveau" title="Marches : 40 cité · 75 montré en mission · 100 démontré">
      <div className="opt-niveau__piste" role="img" aria-label={`Niveau ${fait} sur 100${vise > fait ? `, visé ${vise}` : ''}`}>
        {vise > fait && <span className="opt-niveau__vise" style={{ left: `${fait}%`, width: `${vise - fait}%` }} />}
        <span className="opt-niveau__fait" style={{ width: `${fait}%` }} />
        <span className="opt-niveau__marche" style={{ left: '40%' }} />
        <span className="opt-niveau__marche" style={{ left: '75%' }} />
      </div>
      <span className="opt-niveau__chiffres">{vise > fait ? `${fait} → ${vise}` : fait}</span>
    </div>
  );
}

function Reco({ r, n, seule = false }) {
  const gain = Number(r.gain) || 0;
  return (
    <div className="opt-reco">
      <span className="opt-reco__n">{n}</span>
      <div className="opt-reco__corps">
        <div className="opt-reco__titre">{r.titre}</div>
        {r.aide && <div className="opt-reco__aide">{r.aide}</div>}
        {(r.eliminatoire || r.a_confirmer || r.ecart_entretien || REPONSE[r.reponse_candidat]) && (
          <div className="opt-reco__puces">
            {r.eliminatoire && <span className="opt-puce opt-puce--indispensable">Indispensable</span>}
            {r.ecart_entretien && !REPONSE[r.reponse_candidat] && <span className="opt-puce"><BI icon="chat-left-dots" /> Écart à aborder en entretien</span>}
            {r.a_confirmer && !r.deja_compte && !REPONSE[r.reponse_candidat] && <span className="opt-puce opt-puce--confirmer"><BI icon="chat-left-text" /> À confirmer avec le candidat</span>}
            {r.a_confirmer && r.deja_compte && !REPONSE[r.reponse_candidat] && <span className="opt-puce" title="Déjà montré en mission : un « Oui » fait écrire les mots de l’exigence, sans changer le score"><BI icon="check2" /> Déjà compté</span>}
            {REPONSE[r.reponse_candidat] && <span className={`opt-puce${r.reponse_candidat === 'confirme' ? ' opt-puce--ok' : ''}`}>{REPONSE[r.reponse_candidat]}</span>}
          </div>
        )}
        {r.preuve && <div className="opt-reco__preuve" title={r.preuve}>CV : {r.preuve}</div>}
      </div>
      <span className={`opt-gain${gain > 0 ? '' : ' opt-gain--nul'}`}
        title={seule ? 'Seule, sans les autres indispensables : le bloc reste perdu' : "Gain estimé sur l'adéquation, calculé par la formule du score"}>
        {seule ? `seule : +${gain}` : `+${gain} pts`}
      </span>
      <Niveau niveau={r.niveau} cible={r.cible} />
    </div>
  );
}

// L'OPTIMISEUR DE CV (maquette du 30.10) : le grand panneau de la revue et
// de la machine. Trois onglets — Analyse (adéquation en l'état, potentiel,
// points à améliorer, et une action par exigence avec son GAIN et sa barre de
// niveau : ce que le CV montre, ce que l'action ajouterait), Version corrigée
// (les réécritures fidèles au CV source), Historique (les CV du couple). Tout
// est SERVI : GET /besoins/:id/score-analyse/:cid (aucun appel à l'IA), les
// suggestions du dossier, les documents de la fiche. L'écran ne recalcule
// aucun gain. Générer la version corrigée et valider restent DEUX gestes : le
// serveur demande de relire avant de valider.
//
// LA MACHINE CV (30.10 : « lancer la machine CV : il génère CV par CV en
// format entreprise selon le besoin, il te dit voici le CV, tu peux le
// télécharger, et ce que tu dois demander au candidat parce que ça manque et
// que ça peut monter le score — ou Suivant ») : la même revue, qui à chaque
// profil GÉNÈRE le CV s'il manque, puis LIT tout de suite ses écarts au lot.
// Rien ne part en tâche de fond : un profil à la fois, sous les yeux (elle
// remplace « Générer N pièces », qui produisait tout sans qu'on voie rien).
export default function RevueCvAo({ aoId, onClose, onFocus, onChange, onGenerer, showNotification, machine = false }) {
  const [data, setData] = useState(null);         // null = lecture | { erreur } | état servi
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(null);
  const [panneau, setPanneau] = useState(false);
  const [choix, setChoix] = useState(false);      // la vue « choisir mes lots »
  // Les réécritures du CV (écarts au lot, mêmes routes que l'assistant du
  // dossier) — { cle, etat, score, cible, verdict, suggestions, choisis,
  // changements }. Rangées par profil (`cle`) : un résultat arrivé après un
  // changement de profil ne s'affiche jamais sur le suivant.
  const [amelio, setAmelio] = useState(null);
  // L'Optimiseur : le grand panneau, ouvert depuis la revue (la machine l'ouvre
  // d'office). `analyse` = GET /besoins/:id/score-analyse/:cid ; `historique`
  // = les CV de la fiche et du couple.
  const [optimiseur, setOptimiseur] = useState(false);
  // « Réduire en bas » (31.10 : « si je clique hors de la fenêtre, ça réduit
  // l'optimiseur, et pour le rouvrir je clique comme sur Windows sur le truc
  // en bas, sans perdre mon travail ») : le panneau est CACHÉ, jamais démonté —
  // ce qui est tapé dans les points à confirmer reste là.
  const [reduit, setReduit] = useState(false);
  const tacheRef = useRef(null);
  const [onglet, setOnglet] = useState('analyse');
  const [analyse, setAnalyse] = useState(null);
  const [historique, setHistorique] = useState(null);
  const [toutes, setToutes] = useState(false);
  const [details, setDetails] = useState(false);   // le bruit, replié
  const cleRef = useRef('');
  const premiere = useRef(true);
  const [boite, setBoite] = useState(null);
  const [place, setPlace] = useState(null);
  const bulleRef = useRef(null);
  const fichierRef = useRef(null);

  const equipe = Array.isArray(data?.mes_lots);
  const profils = useMemo(() => aplatir(data), [data]);
  const courant = profils[index] || null;
  const cle = cleDe(courant);
  cleRef.current = cle;
  const large = machine || optimiseur;

  const charger = useCallback(async (garderSur = null) => {
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/revue-cv`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if ([404, 405, 501].includes(r.status) && (!j.error || /route non trouv/i.test(j.error))) { setData({ erreur: 'La revue des CV arrive côté serveur.' }); return; }
      if (!r.ok || j.success === false) { setData({ erreur: j.error || `La revue n'a pas pu être lue (${r.status}).` }); return; }
      setData(j);
      // À l'ouverture, sans lot à soi : on commence par choisir.
      if (premiere.current) { premiere.current = false; if (Array.isArray(j.mes_lots) && !j.mes_lots.length && (j.lots || []).length) setChoix(true); }
      const liste = aplatir(j);
      if (garderSur) {
        const i = liste.findIndex(p => p.besoin_id === garderSur.besoin_id && p.candidate_id === garderSur.candidate_id);
        if (i >= 0) { setIndex(i); return; }
      }
      // On reprend là où il reste du travail.
      const i = liste.findIndex(p => p.etat !== 'valide');
      setIndex(i >= 0 ? i : 0);
    } catch (e) { setData({ erreur: e.message }); }
  }, [aoId]);
  useEffect(() => { charger(); }, [charger]);

  // Le dossier ouvre le lot du profil et le fait défiler jusqu'à lui.
  useEffect(() => {
    if (!courant) return;
    onFocus?.(courant.besoin_id, courant.candidate_id);
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-revue-cv="${courant.besoin_id}:${courant.candidate_id}"]`);
      el?.scrollIntoView({ block: 'center', behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }, 120);
    return () => clearTimeout(t);
  }, [courant?.besoin_id, courant?.candidate_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // La découpe suit la ligne réelle (scroll, redimensionnement, rendu).
  useLayoutEffect(() => {
    if (!courant) { setBoite(null); return; }
    let vivant = true;
    const mesurer = () => {
      if (!vivant) return;
      const el = document.querySelector(`[data-revue-cv="${courant.besoin_id}:${courant.candidate_id}"]`);
      const r = el?.getBoundingClientRect();
      setBoite(r && r.width > 0 ? { top: r.top, left: r.left, width: r.width, height: Math.min(r.height, window.innerHeight * 0.5) } : null);
    };
    mesurer();
    const t = setInterval(mesurer, 400);
    window.addEventListener('scroll', mesurer, true);
    window.addEventListener('resize', mesurer);
    return () => { vivant = false; clearInterval(t); window.removeEventListener('scroll', mesurer, true); window.removeEventListener('resize', mesurer); };
  }, [courant?.besoin_id, courant?.candidate_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (!boite || !bulleRef.current) { setPlace(null); return; }
    const b = bulleRef.current.getBoundingClientRect();
    const dessous = boite.top + boite.height + MARGE;
    const dessus = boite.top - b.height - MARGE;
    const top = (dessous + b.height < window.innerHeight - MARGE) ? dessous
      : (dessus > MARGE ? dessus : Math.max(MARGE, window.innerHeight - b.height - MARGE));
    const left = Math.min(Math.max(MARGE, boite.left + boite.width / 2 - b.width / 2), window.innerWidth - b.width - MARGE);
    setPlace({ top, left });
  }, [boite, index]);

  const aller = (sens) => setIndex(i => Math.min(Math.max(0, i + sens), Math.max(0, profils.length - 1)));

  // Clavier, comme le tutoriel : Échap ferme, les flèches avancent — jamais
  // dans un champ.
  useEffect(() => {
    const dansUnChamp = (el) => !!el && (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable);
    const surTouche = (e) => {
      if (reduit) return; // réduit en bas : le clavier rend la main à la page
      if (e.key === 'Escape') { e.preventDefault(); onClose?.(); return; }
      if (dansUnChamp(e.target) || e.target?.closest?.('[role="tablist"]')) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); aller(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); aller(-1); }
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (!reduit) bulleRef.current?.focus(); }, [index]); // eslint-disable-line react-hooks/exhaustive-deps
  // Hors du grand panneau, rien à réduire. Réduit : le focus va sur la tâche
  // en bas ; rouvert : il revient au panneau.
  useEffect(() => { if (!large) setReduit(false); }, [large]);
  const dejaReduit = useRef(false);
  useEffect(() => {
    if (reduit) { dejaReduit.current = true; tacheRef.current?.focus(); }
    else if (dejaReduit.current) bulleRef.current?.focus();
  }, [reduit]);

  const urlCv = (p, extra = '') => p?.cv ? avecCle(`${BASE}/candidates/${p.candidate_id}/documents/${p.cv.id}/preview${extra}`) : null;

  const copier = async () => {
    setBusy('copier');
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/revue-cv/texte?besoin_id=${courant.besoin_id}&candidate_id=${courant.candidate_id}`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `Le texte n'a pas pu être préparé (${r.status}).`);
      await navigator.clipboard.writeText(j.texte);
      showNotification?.('Copié : le poste et le CV sont prêts à coller dans votre assistant.', 'success');
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  const decider = async (geste) => {
    setBusy(geste);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/revue-cv/${geste}`, {
        method: 'POST', headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ besoin_id: courant.besoin_id, candidate_id: courant.candidate_id }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `Refusé (${r.status}).`);
      showNotification?.(j.message, 'success');
      const ici = { besoin_id: courant.besoin_id, candidate_id: courant.candidate_id };
      await charger(geste === 'valider' ? null : ici);
      onChange?.();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  const lot = async (geste, besoinId, forcer = false) => {
    setBusy(`${geste}:${besoinId}`);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/revue-cv/lots/${geste}`, {
        method: 'POST', headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ besoin_id: besoinId, ...(forcer ? { forcer: true } : {}) }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409 && j.code === 'lot_pris' && !forcer) {
        setBusy(null);
        if (window.confirm(j.error)) await lot(geste, besoinId, true);
        return;
      }
      if ([404, 405, 501].includes(r.status) && (!j.error || /route non trouv/i.test(j.error))) throw new Error('Le partage des lots arrive côté serveur.');
      if (!r.ok || j.success === false) throw new Error(j.error || `Refusé (${r.status}).`);
      showNotification?.(j.message, 'success');
      await charger(courant ? { besoin_id: courant.besoin_id, candidate_id: courant.candidate_id } : null);
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  // Changer de profil replie la liste des actions.
  useEffect(() => { setToutes(false); setDetails(false); }, [cle]);
  // La machine : une génération au plus par profil et par ouverture (un échec
  // ne boucle pas : le bouton reste là pour réessayer).
  const machineFaits = useRef(new Set());

  // L'ANALYSE DU SCORE — servie, point par point (aucun appel à l'IA).
  const chargerAnalyse = async (p) => {
    if (!p) return;
    const k = cleDe(p);
    setAnalyse(a => (a?.cle === k && a.etat === 'ready' ? { ...a, rafraichit: true } : { cle: k, etat: 'loading' }));
    try {
      const r = await fetch(`${BASE}/besoins/${p.besoin_id}/score-analyse/${p.candidate_id}`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (cleRef.current !== k) return;
      if ([404, 405, 501].includes(r.status) && (!j.error || /route non trouv/i.test(j.error))) { setAnalyse({ cle: k, etat: 'route' }); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || `L'analyse n'a pas pu être lue (${r.status}).`);
      if (j.score == null) { setAnalyse({ cle: k, etat: 'absent', message: j.message || "Pas encore de score pour ce profil sur ce besoin : lancez le matching, puis rouvrez l'analyse." }); return; }
      setAnalyse({ ...j, cle: k, etat: 'ready' });
    } catch (e) { if (cleRef.current === k) setAnalyse({ cle: k, etat: 'error', erreur: e.message }); }
  };

  // L'HISTORIQUE — les CV de la fiche et de ce couple, du plus récent.
  const chargerHistorique = async (p) => {
    if (!p) return;
    const k = cleDe(p);
    setHistorique({ cle: k, etat: 'loading' });
    try {
      const r = await fetch(`${BASE}/candidates/${p.candidate_id}/documents`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (cleRef.current !== k) return;
      if (!r.ok || j.success === false) throw new Error(j.error || `Les versions du CV n'ont pas pu être lues (${r.status}).`);
      const docs = (Array.isArray(j.data) ? j.data : [])
        .filter(d => estCv(d) && (d.besoin_id == null || Number(d.besoin_id) === Number(p.besoin_id)))
        .sort((a, b) => Date.parse(b.created_at || 0) - Date.parse(a.created_at || 0));
      setHistorique({ cle: k, etat: 'ready', docs });
    } catch (e) { if (cleRef.current === k) setHistorique({ cle: k, etat: 'error', erreur: e.message }); }
  };

  // LES RÉÉCRITURES — GET …/cv/suggestions (le matching peut encore
  // calculer : on réessaie), puis POST …/cv/apply { ids, besoin_id } qui
  // réécrit le CV format entreprise. L'écran ne fait que montrer et choisir.
  const applicable = (sg) => sg.type !== 'ecart_reel';
  const analyser = async (p = courant, tentative = 0) => {
    if (!p) return;
    const k = cleDe(p);
    if (tentative === 0) chargerAnalyse(p);
    if (!p.cv) { setAmelio({ cle: k, etat: 'sans_cv' }); return; }
    setAmelio(a => ({ ...(a?.cle === k ? a : {}), cle: k, etat: tentative ? 'computing' : 'loading' }));
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/candidates/${p.candidate_id}/cv/suggestions?besoin_id=${p.besoin_id}`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (cleRef.current !== k) return;
      if (!r.ok || j.success === false) throw new Error(j.error || `L'analyse n'a pas pu se faire (${r.status}).`);
      if (j.status === 'computing') {
        if (tentative < 24) { setTimeout(() => { if (cleRef.current === k) analyser(p, tentative + 1); }, 2500); return; }
        throw new Error('Le score de ce profil se calcule encore : réessayez dans une minute.');
      }
      const sugs = Array.isArray(j.suggestions) ? j.suggestions : [];
      setAmelio({ cle: k, etat: 'ready', score: j.score ?? null, cible: j.cible ?? null, verdict: j.verdict || null, suggestions: sugs,
        choisis: new Set(sugs.filter(applicable).map(sg => sg.id)), changements: null });
      // Le score vient d'être calculé : l'analyse point par point se relit.
      if (tentative > 0) chargerAnalyse(p);
    } catch (e) { if (cleRef.current === k) setAmelio({ cle: k, etat: 'error', erreur: e.message }); }
  };
  // RELANCER — recalcule VRAIMENT le score de ce profil (30.10 : « le bouton
  // Relancer ne fait rien ») : il ne faisait que relire le score déjà
  // calculé. Il relance le matching du lot (POST …/matching/recompute, même
  // geste que « Recalculer » sur le dossier, sans force) : un profil dont le
  // CV ou la fiche ont changé est re-noté, les autres sortent du cache sans
  // coût. On attend la fin du calcul, puis l'analyse et les réécritures se
  // relisent — et l'écran DIT si rien n'a changé.
  const relancer = async (p = courant) => {
    if (!p) return;
    const k = cleDe(p);
    const avant = analyse?.cle === k ? analyse.calcule_le || null : null;
    setBusy('relance');
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/matching/recompute?besoin_id=${p.besoin_id}`, {
        method: 'POST', headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ besoin_id: p.besoin_id }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `Le recalcul n'a pas pu être lancé (${r.status}).`);
      // Le calcul tourne en fond : on suit le matching du dossier (3 min au plus).
      let fini = false;
      for (let i = 0; i < 90 && !fini; i++) {
        await new Promise(ok => setTimeout(ok, 2000));
        if (cleRef.current !== k) return;
        const m = await fetch(`${BASE}/ao/${aoId}/matching`, { headers: authH() }).then(x => x.json()).catch(() => null);
        fini = !!m && m.status !== 'computing';
      }
      if (cleRef.current !== k) return;
      if (!fini) { showNotification?.('Le score se calcule encore : l’analyse se mettra à jour à la prochaine ouverture.', 'info'); return; }
      const lu = await fetch(`${BASE}/besoins/${p.besoin_id}/score-analyse/${p.candidate_id}`, { headers: authH() }).then(x => x.json()).catch(() => null);
      if (cleRef.current !== k) return;
      if (lu?.success !== false && lu?.score != null) setAnalyse({ ...lu, cle: k, etat: 'ready' });
      else await chargerAnalyse(p);
      analyser(p);
      onChange?.();
      const apres = lu?.calcule_le || null;
      showNotification?.(avant && apres && apres === avant
        ? 'Le score est déjà à jour : le CV et la fiche n’ont pas changé depuis le dernier calcul.'
        : `Score recalculé${lu?.score_en_l_etat != null ? ` : ${lu.score_en_l_etat}/100` : ''}.`, 'success');
    } catch (e) { if (cleRef.current === k) showNotification?.(e.message, 'error'); }
    finally { setBusy(b => (b === 'relance' ? null : b)); }
  };
  const basculer = (id) => setAmelio(a => {
    const c = new Set(a.choisis); if (c.has(id)) c.delete(id); else c.add(id);
    return { ...a, choisis: c };
  });
  const appliquer = async () => {
    const ids = [...(amelio?.choisis || [])];
    if (!ids.length || !courant) return;
    const ici = { besoin_id: courant.besoin_id, candidate_id: courant.candidate_id };
    setBusy('appliquer');
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/candidates/${ici.candidate_id}/cv/apply`, {
        method: 'POST', headers: { ...authH(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, besoin_id: ici.besoin_id }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `L'amélioration a été refusée (${r.status}).`);
      const gain = (j.score != null && j.score_avant != null) ? ` (${j.score_avant} → ${j.score})` : '';
      showNotification?.(`${ids.length} réécriture${ids.length > 1 ? 's' : ''} appliquée${ids.length > 1 ? 's' : ''} — version corrigée générée${gain}. Relisez-la avant de valider.`, 'success');
      const faites = new Set((j.appliquees || ids).map(Number));
      setAmelio(a => ({ ...a, score: j.score ?? a.score, choisis: new Set(),
        suggestions: a.suggestions.map(sg => (faites.has(Number(sg.id)) ? { ...sg, faite: true } : sg)),
        changements: Array.isArray(j.changements) && j.changements.length ? j.changements : null }));
      await charger(ici);
      chargerAnalyse(ici);
      chargerHistorique(ici);
      onChange?.();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  // Pas de CV format entreprise : on le génère d'ici (le dossier fait le
  // geste — même route que « Générer » sur la ligne), puis on relit.
  const generer = async () => {
    if (!onGenerer || !courant) return;
    setBusy('generer');
    const ici = { besoin_id: courant.besoin_id, candidate_id: courant.candidate_id };
    try { await onGenerer(ici.besoin_id, ici.candidate_id); await charger(ici); setHistorique(null); }
    finally { setBusy(null); }
  };

  // Le panneau ouvert lit tout seul ce qu'il montre, profil par profil ; il
  // génère d'abord le CV format entreprise qui manque — en Machine CV comme
  // dans l'Optimiseur (31.10, Omar T. : « l'optimiseur doit vérifier si c'est
  // fait, et sinon générer automatiquement le CV format entreprise »). Le CV
  // RETENU peut être le CV d'origine désigné pour le besoin : le serveur le
  // dit (`format_entreprise: false`). Une fois par profil et par ouverture.
  const sansCvEntreprise = !!courant && (!courant.cv || courant.cv.format_entreprise === false);
  useEffect(() => {
    if (!large || !courant || busy || choix) return;
    const k = cleDe(courant);
    if (sansCvEntreprise && onGenerer && !machineFaits.current.has(`gen:${k}`)) {
      machineFaits.current.add(`gen:${k}`); generer(); return;
    }
    // analyser() relit aussi l'analyse du score : pas de double lecture.
    if (amelio?.cle !== k || (amelio.etat === 'sans_cv' && courant.cv)) analyser(courant);
    else if (analyse?.cle !== k) chargerAnalyse(courant);
    if (historique?.cle !== k) chargerHistorique(courant);
  }, [large, cle, courant?.cv?.id, sansCvEntreprise, busy, choix, analyse?.cle, amelio?.cle, amelio?.etat, historique?.cle]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ce qu'il faut demander au candidat : les écarts que le CV ne peut pas
  // combler honnêtement — copiables d'un clic pour le message au candidat.
  const am = amelio?.cle === cle ? amelio : null;
  const an = analyse?.cle === cle ? analyse : null;
  const hi = historique?.cle === cle ? historique : null;
  const aDemander = (am?.suggestions || []).filter(sg => !applicable(sg));
  const recos = an?.etat === 'ready' && Array.isArray(an.recommandations) ? an.recommandations : [];
  // À confirmer avec le candidat : servi (`a_confirmer`), sinon reconnu à son
  // geste (serveur d'avant le champ), sinon les écarts réels des suggestions.
  const aConfirmer = an?.etat === 'ready'
    ? recos.filter(r => (typeof r.a_confirmer === 'boolean' ? r.a_confirmer : /^(Demander|Rattacher)\b/.test(String(r.titre || ''))))
      .map(r => ({ cle: `a:${r.critere}`, libelle: r.critere, question: r.question || questionDe(r.critere), gain: r.gain, deja_compte: !!r.deja_compte }))
    : aDemander.map(sg => ({ cle: `s:${sg.id}`, libelle: critereDeSuggestion(sg), question: sg.question || questionDe(critereDeSuggestion(sg)), gain: sg.gain }));
  const remplacer = async (fichier) => {
    if (!fichier) return;
    setBusy('remplacer');
    try {
      const fd = new FormData();
      fd.append('file', fichier);
      fd.append('besoin_id', String(courant.besoin_id));
      fd.append('candidate_id', String(courant.candidate_id));
      const r = await fetch(`${BASE}/ao/${aoId}/revue-cv/remplacer`, { method: 'POST', headers: authH(), body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `Le remplacement a été refusé (${r.status}).`);
      showNotification?.(j.message, 'success');
      setAmelio(null); setAnalyse(null); setHistorique(null);
      await charger();
      onChange?.();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  if (data == null) return (
    <div className="tuto-calque"><div className="tuto-voile" />
      <div className="tuto-bulle tuto-bulle--centre" role="status"><p className="tuto-bulle__texte"><BI icon="arrow-repeat" /> Lecture des CV du dossier…</p></div>
    </div>
  );
  if (!data.erreur && equipe && (choix || !profils.length) && (data.lots || []).length) {
    const miens = data.lots.filter(l => l.mien);
    return (
      <div className="tuto-calque"><div className="tuto-voile" />
        <div className="tuto-bulle tuto-bulle--centre" role="dialog" aria-labelledby="revue-titre" style={{ width: 'min(480px, calc(100vw - 24px))' }}>
          <div className="tuto-bulle__kicker"><span>Revue des CV</span><span className="tuto-bulle__compte">{data.valides}/{data.total} validés</span><button type="button" className="tuto-panneau__x" onClick={onClose} aria-label="Fermer" title="Fermer (Échap)" style={{ marginLeft: 'auto', width: '26px', height: '26px' }}><BI icon="x-lg" /></button></div>
          <h2 id="revue-titre" className="tuto-bulle__titre">Choisissez vos lots</h2>
          <p className="tuto-bulle__texte">Chacun prend ses lots : la revue ne vous emmène que dans les vôtres, et vos collègues voient qui relit quoi.</p>
          <div style={{ maxHeight: '46vh', overflowY: 'auto', margin: '4px 0 8px' }}>
            {data.lots.map(l => {
              const qui = relecteurDe(l);
              const enCours = busy && busy.endsWith(`:${l.besoin_id}`);
              return (
                <div key={l.besoin_id} className="erp-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 2px', borderBottom: '1px solid var(--erp-line)' }}>
                  <BI icon={l.mien ? 'person-check-fill' : l.relecteur ? 'person-fill' : 'person'} style={{ color: l.mien ? 'var(--erp-success)' : 'var(--erp-text-secondary)' }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: 'var(--erp-ink)' }}>{l.titre}</span>
                    <span style={{ fontSize: '11px', color: 'var(--erp-text-tertiary)' }}>
                      <span className="ats-mono">{l.valides}/{l.total}</span> validés · {qui ? `relu par ${qui === 'Vous' ? 'vous' : qui}` : 'libre'}
                    </span>
                  </span>
                  {l.mien
                    ? <button type="button" className="tuto-bulle__lien" onClick={() => lot('lacher', l.besoin_id)} disabled={!!busy}>{enCours ? <BI icon="arrow-repeat" /> : null} Lâcher</button>
                    : <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => lot('prendre', l.besoin_id)} disabled={!!busy}>
                        <BI icon={enCours ? 'arrow-repeat' : 'plus-lg'} /> {l.relecteur ? 'Reprendre' : 'Prendre'}
                      </button>}
                </div>
              );
            })}
          </div>
          <div className="tuto-bulle__pied">
            <button type="button" className="tuto-bulle__lien" onClick={onClose}>Fermer</button>
            <span className="tuto-bulle__espace" />
            <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" disabled={!profils.length || !!busy}
              onClick={() => { setChoix(false); const i = profils.findIndex(p => p.etat !== 'valide'); setIndex(i >= 0 ? i : 0); }}>
              {miens.length ? (profils.length ? `Relire mes lots (${profils.length} profil${profils.length > 1 ? 's' : ''})` : 'Vos lots sont vides') : 'Prenez au moins un lot'}
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (data.erreur || !profils.length) return (
    <div className="tuto-calque"><div className="tuto-voile" />
      <div className="tuto-bulle tuto-bulle--centre" role="dialog" aria-labelledby="revue-titre">
        <div className="tuto-bulle__kicker"><span>Revue des CV</span><button type="button" className="tuto-panneau__x" onClick={onClose} aria-label="Fermer" title="Fermer (Échap)" style={{ marginLeft: 'auto', width: '26px', height: '26px' }}><BI icon="x-lg" /></button></div>
        <h2 id="revue-titre" className="tuto-bulle__titre">{data.erreur ? 'La revue ne s’ouvre pas' : 'Aucun profil à revoir'}</h2>
        <p className="tuto-bulle__texte">{data.erreur || 'Positionnez des profils sur les besoins du dossier : leurs CV apparaîtront ici.'}</p>
        <div className="tuto-bulle__pied"><span className="tuto-bulle__espace" />
          <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={onClose}>Fermer</button></div>
      </div>
    </div>
  );

  const pointe = !!boite;
  // L'Optimiseur (ou la machine) : la bulle devient un grand panneau à droite
  // (30.10 : « la fenêtre est trop petite »).
  const valide = courant.etat === 'valide';
  const applicables = (am?.suggestions || []).filter(sg => applicable(sg) && !sg.faite);
  const nChoisis = am?.etat === 'ready' ? [...am.choisis].filter(id => applicables.some(sg => sg.id === id)).length : 0;
  const groupe = (an?.groupes || [])[0] || null;
  const dansGroupe = groupe ? recos.filter(r => r.groupe === 'indispensables') : [];
  const horsGroupe = groupe ? recos.filter(r => r.groupe !== 'indispensables') : recos;
  // D'abord ce qui débloque le bloc des indispensables, puis l'utile (ordre du
  // serveur), et le bruit replié.
  const debloquent = horsGroupe.filter(r => r.debloque);
  const utiles = horsGroupe.filter(r => !r.debloque && !estBruit(r));
  const bruit = horsGroupe.filter(r => !r.debloque && estBruit(r));
  const visibles = toutes ? utiles : utiles.slice(0, 3);
  // Après une écriture dans le CV ou une relecture : le CV se reconstruit avec
  // les ajouts (même geste que « Générer »), puis l'analyse se relit.
  const apresEcriture = async (j) => {
    if (j?.a_regenerer && onGenerer) await generer();
    if (courant) analyser(courant);
  };
  const docCv = courant.cv && hi?.etat === 'ready' ? (hi.docs || []).find(d => Number(d.id) === Number(courant.cv.id)) : null;
  const surToucheOnglet = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = ONGLETS.findIndex(o => o[0] === onglet);
    const suivant = ONGLETS[(i + (e.key === 'ArrowRight' ? 1 : -1) + ONGLETS.length) % ONGLETS.length][0];
    setOnglet(suivant);
    document.getElementById(`opt-onglet-${suivant}`)?.focus();
  };
  const navigation = (
    <div className="tuto-bulle__pied">
      <button type="button" className="tuto-bulle__lien" onClick={onClose}>Fermer</button>
      <button type="button" className="tuto-bulle__lien" onClick={() => setPanneau(true)}>
        {data.valides}/{data.total} validés — voir tout
      </button>
      {equipe && <button type="button" className="tuto-bulle__lien" onClick={() => setChoix(true)}>Mes lots</button>}
      {optimiseur && !machine && <button type="button" className="tuto-bulle__lien" onClick={() => setOptimiseur(false)}>Réduire</button>}
      <span className="tuto-bulle__espace" />
      {index > 0 && <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => aller(-1)}>Précédent</button>}
      {index < profils.length - 1
        ? <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => aller(1)}>{valide ? 'Suivant' : 'Plus tard'}</button>
        : <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={onClose}>Terminer</button>}
    </div>
  );
  const boutonRemplacer = (
    <>
      <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => fichierRef.current?.click()} disabled={!!busy}>
        <BI icon={busy === 'remplacer' ? 'arrow-repeat' : 'upload'} /> {courant.cv ? 'Remplacer…' : 'Déposer un CV…'}
      </button>
      <input ref={fichierRef} type="file" accept=".docx,.doc,.pdf" hidden
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; remplacer(f); }} />
    </>
  );
  return (
    <>
      <div className={`tuto-calque${large && reduit ? ' is-reduit' : ''}`}>
        {pointe
          ? <div className="tuto-decoupe" aria-hidden="true" style={{ top: `${boite.top - 4}px`, left: `${boite.left - 4}px`, width: `${boite.width + 8}px`, height: `${boite.height + 8}px` }} />
          : !large && <div className="tuto-voile" aria-hidden="true" />}
        {/* Grand panneau : un clic hors de lui le réduit en bas, et ne traverse plus vers la page. */}
        {large && <div className={`tuto-attrape${pointe ? '' : ' tuto-attrape--voile'}`} aria-hidden="true" onMouseDown={() => setReduit(true)} />}
        <div ref={bulleRef} tabIndex={-1} role="dialog" aria-labelledby="revue-titre"
          className={`tuto-bulle${large ? ' tuto-bulle--large' : pointe ? '' : ' tuto-bulle--centre'}`}
          style={large ? undefined : { width: 'min(420px, calc(100vw - 24px))', ...(pointe && place ? { top: `${place.top}px`, left: `${place.left}px` } : {}) }}>
          {large ? (
            <>
              <div className="tuto-bulle__kicker">
                <span>Optimiseur de CV · {courant.lot}</span>
                <span className="tuto-bulle__compte">{index + 1}/{profils.length}</span>
                <button type="button" className="tuto-panneau__x" onClick={() => setReduit(true)} aria-label="Réduire en bas" title="Réduire en bas (votre travail est gardé)" style={{ marginLeft: 'auto', width: '26px', height: '26px' }}><BI icon="dash-lg" /></button>
                <button type="button" className="tuto-panneau__x" onClick={onClose} aria-label="Fermer" title="Fermer (Échap)" style={{ width: '26px', height: '26px' }}><BI icon="x-lg" /></button>
              </div>
              <h2 id="revue-titre" className="tuto-bulle__titre">{courant.nom}</h2>
              {data.ao?.titre && <p className="opt-sous">{data.ao.titre} · {courant.lot}</p>}
              <p className="tuto-bulle__texte" style={{ color: TON[courant.etat] }}>
                <BI icon={ICONE[courant.etat]} /> {courant.etat_libelle}
                {valide && courant.valide?.par ? ` — par ${courant.valide.par}` : ''}
              </p>
              {busy === 'generer' && (
                <p className="tuto-bulle__absent"><BI icon="arrow-repeat" /> {courant.cv && courant.cv.format_entreprise === false
                  ? 'Le CV retenu est le CV d’origine : génération du CV format entreprise pour ce besoin… (une à deux minutes)'
                  : 'Génération du CV format entreprise pour ce besoin… (une à deux minutes)'}</p>
              )}

              <div className="opt-onglets" role="tablist" aria-label="Optimiseur de CV" onKeyDown={surToucheOnglet}>
                {ONGLETS.map(([id, libelle]) => (
                  <button key={id} id={`opt-onglet-${id}`} type="button" role="tab" className="opt-onglet"
                    aria-selected={onglet === id} aria-controls={`opt-panneau-${id}`} tabIndex={onglet === id ? 0 : -1}
                    onClick={() => setOnglet(id)}>
                    {libelle}
                    {id === 'corrigee' && applicables.length > 0 && <span className="opt-onglet__n">{applicables.length}</span>}
                    {id === 'historique' && hi?.etat === 'ready' && hi.docs.length > 0 && <span className="opt-onglet__n">{hi.docs.length}</span>}
                  </button>
                ))}
              </div>

              {onglet === 'analyse' && (
                <div id="opt-panneau-analyse" role="tabpanel" aria-labelledby="opt-onglet-analyse">
                  <section className="opt-section">
                    <div className="opt-section__tete">
                      <h3 className="opt-section__titre">Résultat de l’analyse</h3>
                      {an?.etat === 'ready' && an.calcule_le && <span className="opt-section__meta">Analysé le {dateHeure(an.calcule_le)}</span>}
                      <span className="tuto-bulle__espace" />
                      <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => relancer(courant)}
                        disabled={!!busy || an?.etat === 'loading' || am?.etat === 'loading' || am?.etat === 'computing'}
                        title="Recalcule le score de ce profil sur ce besoin (un CV ou une fiche inchangés ne coûtent rien)">
                        {busy === 'relance' ? <i className="bi bi-arrow-repeat tq-spin" aria-hidden="true" /> : <BI icon="arrow-repeat" />} {busy === 'relance' ? 'Recalcul…' : 'Relancer'}
                      </button>
                    </div>
                    {(!an || an.etat === 'loading') && <ChargementAtomes libelle="Lecture du score de ce profil sur ce besoin…" />}
                    {an?.etat === 'route' && <p className="opt-muet">L’analyse point par point arrive côté serveur.</p>}
                    {an?.etat === 'absent' && (am?.etat === 'computing'
                      ? <ChargementAtomes libelle="Le score du profil se calcule…" />
                      : <p className="opt-muet">{an.message}</p>)}
                    {busy === 'relance' && <ChargementAtomes libelle="Recalcul du score de ce profil… (un CV ou une fiche inchangés ne coûtent rien)" />}
                    {an?.etat === 'error' && <p className="opt-erreur">{an.erreur}</p>}
                    {an?.etat === 'ready' && (
                      <>
                        {an.score_perime && (
                          <div className="opt-bandeau opt-bandeau--perime" role="status">
                            <BI icon="clock-history" />
                            <span>{an.message || 'Le CV a été modifié après ce score : relancez le matching.'}</span>
                            <button type="button" className="tuto-bulle__lien" onClick={() => relancer(courant)} disabled={!!busy}>
                              {busy === 'relance' ? 'Recalcul…' : 'Relancer le matching'}
                            </button>
                          </div>
                        )}
                        <div className="opt-tuiles">
                          <div className="opt-tuile">
                            <div className="opt-tuile__val">{an.score_en_l_etat ?? an.score}<small>/100</small></div>
                            <div className="opt-tuile__lib">Adéquation actuelle</div>
                            <div className="opt-tuile__sous">ce que le CV démontre aujourd’hui</div>
                          </div>
                          <div className="opt-tuile opt-tuile--potentiel">
                            <div className="opt-tuile__val">{an.score_potentiel != null ? <>{an.score_potentiel}<small>/100</small></> : '—'}</div>
                            <div className="opt-tuile__lib">Potentiel</div>
                            <div className="opt-tuile__sous">{an.score_potentiel != null ? 'si les points à confirmer le sont' : 'aucun point à confirmer'}</div>
                          </div>
                          <div className="opt-tuile">
                            <div className="opt-tuile__val">{an.a_ameliorer ?? 0}</div>
                            <div className="opt-tuile__lib">Points à améliorer</div>
                            <div className="opt-tuile__sous">sur {an.exigences_total ?? 0} exigence{(an.exigences_total ?? 0) > 1 ? 's' : ''}</div>
                          </div>
                        </div>
                        {ligneGrille(an.grille) && <p className="opt-grille">{ligneGrille(an.grille)}</p>}
                        {an.statut_ao && an.statut_ao_libelle && (
                          <div className="opt-bandeau opt-bandeau--ao" role="note">
                            <BI icon="exclamation-octagon" />
                            <span>{an.statut_ao_libelle}</span>
                          </div>
                        )}
                      </>
                    )}
                  </section>

                  {an?.etat === 'ready' && (
                    <section className="opt-section">
                      <div className="opt-section__tete"><h3 className="opt-section__titre">Actions recommandées</h3>
                        {recos.length > 0 && <span className="opt-section__meta">{recos.length} au total</span>}</div>
                      {!recos.length && <p className="opt-muet">Rien à faire monter : chaque exigence est démontrée par le CV.</p>}
                      {groupe && (
                        <div className="opt-groupe">
                          <div className="opt-groupe__tete">
                            <span className="opt-groupe__titre">{groupe.libelle}</span>
                            <span className={`opt-gain${groupe.gain > 0 ? '' : ' opt-gain--nul'}`} title="Gain du groupe, calculé par la formule du score">+{groupe.gain} pts</span>
                          </div>
                          {groupe.aide && <p className="opt-groupe__aide">{groupe.aide}</p>}
                          {dansGroupe.map((r, i) => <Reco key={`g:${r.critere}`} r={r} n={i + 1} seule />)}
                        </div>
                      )}
                      {debloquent.map((r, i) => <Reco key={`d:${r.critere}`} r={r} n={dansGroupe.length + i + 1} />)}
                      {visibles.map((r, i) => <Reco key={`r:${r.critere}`} r={r} n={dansGroupe.length + debloquent.length + i + 1} />)}
                      {utiles.length > 3 && (
                        <button type="button" className="tuto-bulle__lien opt-plus" onClick={() => setToutes(v => !v)}>
                          {toutes ? 'Réduire la liste' : `Voir toutes les actions (${utiles.length})`}
                        </button>
                      )}
                      {bruit.length > 0 && (
                        <div className="opt-details">
                          <button type="button" className="tuto-bulle__lien opt-plus" aria-expanded={details} onClick={() => setDetails(v => !v)}>
                            <BI icon={details ? 'chevron-up' : 'chevron-down'} /> {details ? 'Masquer les détails' : `Voir les détails (${bruit.length})`}
                          </button>
                          {!details && <p className="opt-note">Moins d’un point chacune, ou des savoir-être à observer en entretien.</p>}
                          {details && bruit.map((r, i) => <Reco key={`b:${r.critere}`} r={r} n={dansGroupe.length + debloquent.length + utiles.length + i + 1} />)}
                        </div>
                      )}
                    </section>
                  )}

                  {(an?.etat === 'ready' || aConfirmer.length > 0) && (
                    <PointsAConfirmer key={cle} besoinId={courant.besoin_id} candidateId={courant.candidate_id}
                      prenom={courant?.nom?.split(' ')[0] || ''} points={aConfirmer} aCv={!!courant.cv} peutGenerer={!!onGenerer} regenEnCours={busy === 'generer'}
                      niveaux={Object.fromEntries(recos.map(r => [r.critere, Number(r.niveau) || 0]))}
                      ecarts={Array.isArray(an?.ecarts_entretien) ? an.ecarts_entretien : recos.filter(r => r.ecart_entretien).map(r => r.critere)}
                      onEcrit={() => chargerAnalyse(courant)} onReponses={() => chargerAnalyse(courant)}
                      onRegenerer={() => apresEcriture({ a_regenerer: true })} showNotification={showNotification} />
                  )}

                  <QuestionsCandidat key={`q:${cle}`} besoinId={courant.besoin_id} candidateId={courant.candidate_id}
                    onReponses={() => chargerAnalyse(courant)} onRegenerer={() => apresEcriture({ a_regenerer: true })}
                    peutGenerer={!!onGenerer} regenEnCours={busy === 'generer'} showNotification={showNotification} />

                  <section className="opt-section">
                    <div className="opt-section__tete"><h3 className="opt-section__titre">Fichier à améliorer</h3></div>
                    {courant.cv ? (
                      <div className="opt-fichier">
                        <BI icon={/pdf/i.test(courant.cv.mime_type || courant.cv.nom || '') ? 'file-earmark-pdf' : 'file-earmark-word'} />
                        <div className="opt-fichier__corps">
                          <div className="opt-fichier__nom" title={courant.cv.nom}>{courant.cv.nom}</div>
                          <div className="opt-fichier__meta">{[courant.cv.raison, dateHeure(courant.cv.maj), taille(docCv?.file_size)].filter(Boolean).join(' · ')}</div>
                        </div>
                        <div className="opt-fichier__liens">
                          <a href={urlCv(courant)} target="_blank" rel="noopener noreferrer">Ouvrir</a>
                          <a href={urlCv(courant, '?download=1&format=word')}>Télécharger (Word)</a>
                        </div>
                      </div>
                    ) : (
                      <p className="opt-muet">
                        Pas encore de CV format entreprise pour ce besoin.{' '}
                        {onGenerer ? 'Générez-le (« Générer le CV format entreprise »), ou déposez votre version.' : 'Générez-le depuis sa ligne du dossier, puis revenez ici.'}
                      </p>
                    )}
                  </section>

                </div>
              )}

              {onglet === 'corrigee' && (
                <div id="opt-panneau-corrigee" role="tabpanel" aria-labelledby="opt-onglet-corrigee">
                  <section className="opt-section">
                    <div className="opt-section__tete"><h3 className="opt-section__titre">Réécritures proposées</h3>
                      {am?.etat === 'ready' && am.score != null && <span className="opt-section__meta">score {am.score}{typeof am.cible === 'number' ? ` → ${am.cible}` : ''}</span>}</div>
                    <p className="opt-note">Chaque réécriture reste fidèle au CV source : ce qui n’y est pas écrit est retiré, et dit.</p>
                    {(!am || am.etat === 'sans_cv') && <p className="opt-muet">Générez d’abord le CV format entreprise de ce besoin : les réécritures portent sur lui.</p>}
                    {(am?.etat === 'loading' || am?.etat === 'computing') && <ChargementAtomes libelle={am.etat === 'computing' ? 'Le score du profil se calcule…' : 'Lecture des écarts du CV face au lot…'} />}
                    {am?.etat === 'error' && <p className="opt-erreur">{am.erreur}</p>}
                    {am?.etat === 'ready' && (
                      <>
                        {am.verdict && <p className="opt-muet">{am.verdict}</p>}
                        {!am.suggestions.some(applicable) && <p className="opt-muet">Rien à réécrire : les écarts restants se demandent au candidat (onglet Analyse).</p>}
                        {am.suggestions.filter(applicable).map(sg => (
                          <label key={sg.id} className="opt-sugg" style={{ cursor: sg.faite ? 'default' : 'pointer' }}>
                            {sg.faite
                              ? <BI icon="check-circle-fill" style={{ marginTop: '2px', color: 'var(--erp-success)' }} />
                              : <input type="checkbox" checked={am.choisis.has(sg.id)} onChange={() => basculer(sg.id)} disabled={!!busy} />}
                            <span style={{ flex: 1, minWidth: 0 }}>
                              <span className="opt-sugg__titre">{sg.titre}{sg.gain != null && <span className="ats-mono" style={{ fontWeight: 400, color: 'var(--erp-text-tertiary)' }}> +{sg.gain} pts</span>}</span>
                              {sg.why && <span className="opt-sugg__why">{sg.why}</span>}
                              {sg.faite && <span className="opt-sugg__why" style={{ color: 'var(--erp-success)' }}>Appliquée au CV</span>}
                            </span>
                          </label>
                        ))}
                        {am.changements && (
                          <div className="opt-section" style={{ marginTop: '10px' }}>
                            <h3 className="opt-section__titre">Modifié dans le CV</h3>
                            {am.changements.map((ch, i) => <div key={i} className="opt-question">· {typeof ch === 'string' ? ch : (ch.texte || ch.message || ch.label || '')}</div>)}
                          </div>
                        )}
                      </>
                    )}
                  </section>
                </div>
              )}

              {onglet === 'historique' && (
                <div id="opt-panneau-historique" role="tabpanel" aria-labelledby="opt-onglet-historique">
                  <section className="opt-section">
                    <div className="opt-section__tete"><h3 className="opt-section__titre">Relecture</h3></div>
                    <p className="opt-muet" style={{ color: valide ? 'var(--erp-success)' : undefined }}>
                      <BI icon={valide ? 'check-circle-fill' : 'circle'} />{' '}
                      {courant.valide?.le ? `Validé le ${dateHeure(courant.valide.le)}${courant.valide.par ? ` par ${courant.valide.par}` : ''}` : 'Pas encore validé.'}
                    </p>
                  </section>
                  <section className="opt-section">
                    <div className="opt-section__tete"><h3 className="opt-section__titre">Ajouts validés</h3>
                      <span className="opt-section__meta">qui, quand, et la réponse du candidat</span></div>
                    <AjoutsValides key={cle} besoinId={courant.besoin_id} candidateId={courant.candidate_id} />
                  </section>
                  <section className="opt-section">
                    <div className="opt-section__tete"><h3 className="opt-section__titre">Versions du CV</h3>
                      <span className="opt-section__meta">fiche et ce besoin</span></div>
                    {(!hi || hi.etat === 'loading') && <p className="opt-muet"><BI icon="arrow-repeat" /> Lecture des versions…</p>}
                    {hi?.etat === 'error' && <p className="opt-erreur">{hi.erreur}</p>}
                    {hi?.etat === 'ready' && !hi.docs.length && <p className="opt-muet">Aucun CV sur la fiche ni sur ce besoin : déposez celui du candidat.</p>}
                    {hi?.etat === 'ready' && hi.docs.map(d => {
                      const retenu = courant.cv && Number(d.id) === Number(courant.cv.id);
                      return (
                        <div key={d.id} className="opt-version">
                          <BI icon={/pdf/i.test(d.mime_type || d.original_name || '') ? 'file-earmark-pdf' : 'file-earmark-text'} />
                          <div className="opt-version__corps">
                            <div className="opt-fichier__nom" title={d.original_name || d.label}>{d.original_name || d.label || 'CV'}</div>
                            <div className="opt-fichier__meta">{[dateHeure(d.created_at), d.created_by_name, taille(d.file_size)].filter(Boolean).join(' · ')}</div>
                          </div>
                          {retenu && <span className="opt-puce opt-puce--ok">Retenu</span>}
                          <span className="opt-puce">{d.besoin_id == null ? 'Fiche' : 'Ce besoin'}</span>
                          <a className="tuto-bulle__lien" href={avecCle(`${BASE}/candidates/${courant.candidate_id}/documents/${d.id}/preview`)} target="_blank" rel="noopener noreferrer">Ouvrir</a>
                        </div>
                      );
                    })}
                  </section>
                  {am?.changements && (
                    <section className="opt-section">
                      <div className="opt-section__tete"><h3 className="opt-section__titre">Dans cette session</h3></div>
                      {am.changements.map((ch, i) => <div key={i} className="opt-question">· {typeof ch === 'string' ? ch : (ch.texte || ch.message || ch.label || '')}</div>)}
                    </section>
                  )}
                </div>
              )}

              <div className="opt-pied">
                <div className="tuto-bulle__pied">
                  {courant.cv && (
                    <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={copier} disabled={!!busy}>
                      <BI icon={busy === 'copier' ? 'arrow-repeat' : 'clipboard'} /> Copier pour l’IA
                    </button>
                  )}
                  {!courant.cv && onGenerer && (
                    <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={generer} disabled={!!busy}>
                      <BI icon={busy === 'generer' ? 'arrow-repeat' : 'file-earmark-text'} /> {busy === 'generer' ? 'Génération…' : 'Générer le CV format entreprise'}
                    </button>
                  )}
                  {boutonRemplacer}
                  <span className="tuto-bulle__espace" />
                  {nChoisis > 0 ? (
                    <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={appliquer} disabled={!!busy}>
                      <BI icon={busy === 'appliquer' ? 'arrow-repeat' : 'pencil-square'} /> {busy === 'appliquer' ? 'Génération de la version corrigée…' : `Générer la version corrigée (${nChoisis})`}
                    </button>
                  ) : courant.cv && (valide
                    ? <button type="button" className="tuto-bulle__lien" onClick={() => decider('annuler')} disabled={!!busy}>Retirer la validation</button>
                    : <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={() => decider('valider')} disabled={!!busy}>
                        <BI icon={busy === 'valider' ? 'arrow-repeat' : 'check2'} /> Valider le CV
                      </button>)}
                </div>
                {navigation}
              </div>
            </>
          ) : (
            <>
              <div className="tuto-bulle__kicker">
                <span>Revue des CV · {courant.lot}</span>
                <span className="tuto-bulle__compte">{index + 1}/{profils.length}</span>
                <button type="button" className="tuto-panneau__x" onClick={onClose} aria-label="Fermer" title="Fermer (Échap)" style={{ marginLeft: 'auto', width: '26px', height: '26px' }}><BI icon="x-lg" /></button>
              </div>
              <h2 id="revue-titre" className="tuto-bulle__titre">{courant.nom}</h2>
              <p className="tuto-bulle__texte" style={{ color: TON[courant.etat] }}>
                <BI icon={ICONE[courant.etat]} /> {courant.etat_libelle}
                {valide && courant.valide?.par ? ` — par ${courant.valide.par}` : ''}
              </p>
              {busy === 'generer' && (
                <p className="tuto-bulle__texte"><BI icon="arrow-repeat" /> Génération du CV format entreprise pour ce besoin… (une à deux minutes)</p>
              )}
              {courant.cv ? (
                <p className="tuto-bulle__absent">
                  {courant.cv.raison} : <strong>{courant.cv.nom}</strong>.{' '}
                  <a href={urlCv(courant)} target="_blank" rel="noopener noreferrer">Ouvrir</a>
                  {' · '}
                  <a href={urlCv(courant, '?download=1&format=word')}>Télécharger (Word)</a>
                </p>
              ) : (
                <p className="tuto-bulle__absent">
                  {onGenerer
                    ? 'Générez son CV format entreprise ici (quelques secondes), ou déposez votre version (« Déposer un CV… »).'
                    : 'Générez son CV format entreprise depuis sa ligne (« Générer »), puis revenez ici.'}
                </p>
              )}
              {courant.cv && (
                <p className="tuto-bulle__texte">
                  <strong>Copier pour l’IA</strong>, collez dans votre assistant et challengez le CV ; puis <strong>Valider</strong>, ou <strong>Remplacer</strong> par votre version corrigée — elle devient le CV du besoin partout. <strong>Optimiser le CV</strong> montre ce qui le ferait monter, point par point.
                </p>
              )}
              <div className="tuto-bulle__pied">
                {courant.cv && (
                  <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={copier} disabled={!!busy}>
                    <BI icon={busy === 'copier' ? 'arrow-repeat' : 'clipboard'} /> Copier pour l’IA
                  </button>
                )}
                {!courant.cv && onGenerer && (
                  <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={generer} disabled={!!busy}>
                    <BI icon={busy === 'generer' ? 'arrow-repeat' : 'file-earmark-text'} /> {busy === 'generer' ? 'Génération…' : 'Générer le CV format entreprise'}
                  </button>
                )}
                <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => { setOnglet('analyse'); setOptimiseur(true); }} disabled={!!busy}
                  title="Adéquation, potentiel et, exigence par exigence, ce qui ferait monter le score — sans rien inventer">
                  <BI icon="sliders" /> Optimiser le CV
                </button>
                {boutonRemplacer}
                <span className="tuto-bulle__espace" />
                {courant.cv && (valide
                  ? <button type="button" className="tuto-bulle__lien" onClick={() => decider('annuler')} disabled={!!busy}>Retirer la validation</button>
                  : <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={() => decider('valider')} disabled={!!busy}>
                      <BI icon={busy === 'valider' ? 'arrow-repeat' : 'check2'} /> Valider
                    </button>)}
              </div>
              {navigation}
            </>
          )}
        </div>
      </div>

      {large && reduit && (
        <button ref={tacheRef} type="button" className="revue-tache" onClick={() => setReduit(false)}
          title="Rouvrir — votre travail est gardé">
          <BI icon="window-stack" />
          <span className="revue-tache__lib">{machine ? 'Machine CV' : 'Optimiseur de CV'} — {courant.nom}</span>
          <span className="revue-tache__n">{index + 1}/{profils.length}</span>
          {busy && <i className="bi bi-arrow-repeat tq-spin" aria-label="Travail en cours" />}
        </button>
      )}

      {panneau && (
        <>
          <div className="tuto-panneau__fond" onClick={() => setPanneau(false)} aria-hidden="true" />
          <aside className="tuto-panneau erp-drawer" role="dialog" aria-label="Revue des CV">
            <header className="tuto-panneau__tete">
              <h2 className="tuto-panneau__titre">Revue des CV — {data.valides}/{data.total}</h2>
              <button type="button" className="tuto-panneau__x" onClick={() => setPanneau(false)} aria-label="Fermer"><BI icon="x-lg" /></button>
            </header>
            <div style={{ overflowY: 'auto', padding: '4px 16px 16px' }}>
              {data.lots.map(l => (
                <section key={l.besoin_id} style={{ marginTop: '12px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--erp-text-secondary)' }}>
                    {l.titre} <span className="ats-mono">{l.valides}/{l.total}</span>
                    {equipe && <span style={{ textTransform: 'none', fontWeight: 500, color: 'var(--erp-text-tertiary)' }}> · {relecteurDe(l) ? `relu par ${relecteurDe(l) === 'Vous' ? 'vous' : relecteurDe(l)}` : 'libre'}</span>}
                  </div>
                  {l.profils.map(p => {
                    const i = profils.findIndex(x => x.besoin_id === p.besoin_id && x.candidate_id === p.candidate_id);
                    return (
                      <button key={p.candidate_id} type="button" disabled={i < 0} title={i < 0 ? 'Prenez ce lot pour relire ce profil' : undefined}
                        onClick={() => { if (i >= 0) { setIndex(i); setPanneau(false); } }}
                        className="erp-row" style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left', padding: '7px 4px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--erp-line)', font: 'inherit', cursor: i < 0 ? 'default' : 'pointer', color: i < 0 ? 'var(--erp-text-tertiary)' : 'var(--erp-ink)' }}>
                        <BI icon={ICONE[p.etat]} style={{ color: TON[p.etat] }} />
                        <span style={{ flex: 1, minWidth: 0, fontSize: '12.5px', fontWeight: i === index ? 700 : 500 }}>{p.nom}</span>
                        <span style={{ fontSize: '11px', color: 'var(--erp-text-tertiary)' }}>{p.etat_libelle}</span>
                      </button>
                    );
                  })}
                </section>
              ))}
            </div>
          </aside>
        </>
      )}
    </>
  );
}
